import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { isRefusal, refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { ElementRow, Id } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { ProposeAgainDialog, ReproposeDialog } from "../../../app/ProposeAgain";
import { Button, ConfirmDialog, Dialog } from "../../../ui";
import { useCommandId, useRefresh } from "../hooks";
import { canMarkRemoval, canWithdraw, isGone, othersPending } from "../lib/badges";
import { editPath } from "../lib/paths";
import { ElementPicker } from "./ElementPicker";
import { Check, FormError, TextArea, TextInput } from "./fields";

const REMOVAL_REASONS = ["No deliverable needs it any more", "It is wrong or a duplicate", "Another element replaces it", "Out of scope"];

/** What the server says hangs on an element it was asked to withdraw: ids with a name when it gave one. */
export function dependentsFromDetails(details: unknown): Array<{ id: Id; label: string }> {
  if (!details || typeof details !== "object") return [];
  const record = details as Record<string, unknown>;
  const list = [record.dependents, record.elements, record.confirm_dependents].find((entry) => Array.isArray(entry));
  if (!Array.isArray(list)) return [];
  const out: Array<{ id: Id; label: string }> = [];
  for (const entry of list) {
    if (typeof entry === "string") out.push({ id: entry, label: entry });
    else if (entry && typeof entry === "object") {
      const item = entry as Record<string, unknown>;
      const id = typeof item.id === "string" ? item.id : typeof item.element_id === "string" ? item.element_id : null;
      if (!id) continue;
      const label = [item.name, item.code].find((value): value is string => typeof value === "string" && value !== "") ?? id;
      out.push({ id, label });
    }
  }
  return out;
}

interface ActionsProps {
  element: ElementRow;
  /** Only an engineer changes an element. */
  editable: boolean;
  /** How many places use it, from the element view. */
  usedIn?: number;
  className?: string;
}

type Which = "withdraw" | "removal" | "lift" | "drop" | "code" | "not_used" | "same_item" | "repropose" | "propose_again" | null;

/** The things an engineer can do to one element: edit it, withdraw it, mark it for removal, correct its code. */
export function ElementActions({ element, editable, usedIn, className }: ActionsProps) {
  const me = useCurrentMe();
  const [open, setOpen] = useState<Which>(null);
  const close = () => setOpen(null);
  if (!editable) return null;

  // A withdrawn element can be brought back as a card in one of the engineer's sessions (CP-07). A removed one is gone.
  if (element.state === "withdrawn") {
    return (
      <div className={className ?? "element-actions"} role="group" aria-label="What you can do with this element">
        <Button variant="primary" onClick={() => setOpen("propose_again")}>
          Propose again
        </Button>
        {open === "propose_again" && <ProposeAgainDialog element={element} onClose={close} />}
      </div>
    );
  }
  if (isGone(element)) return null;

  const rejected = element.state === "rejected";
  const loaded = element.origin === "loaded";
  const hasPending = !!element.pending;
  // Only the engineer who made a pending change drops it (the server refuses anyone else). Others are told a colleague has one.
  const pendingIsMine = hasPending && !!me.active && element.pending?.made_by === me.active.membership_id;
  // A colleague's Next version waits: the offer "add to it, or wait" takes the place of Edit (UI-11, R2-07).
  const addTo = rejected ? null : othersPending(element, me.active?.membership_id);
  const addedBy = (element.pending_added_by ?? []).map((person) => person.display).filter(Boolean);
  const hasRemoval = !!element.removal;
  // Until the SME first validates it the engineer corrects the code; after that a new code is a Next version that the SME
  // validates (DEC-1016-107). One Next version waits at a time, so the offer waits while another one or a removal mark does.
  // A chapter's code is its key and a document link's code is its item's code and its link (BR-9): the server refuses both.
  const ownCode = element.kind !== "spec_chapter" && element.kind !== "document_link";
  const canCorrectCode = ownCode && !loaded && !element.code_fixed;
  const canProposeCode =
    ownCode && !loaded && !!element.code_fixed && element.state === "validated" && !hasRemoval && (!hasPending || (pendingIsMine && element.pending?.status === "sent_back"));
  return (
    <div className={className ?? "element-actions"} role="group" aria-label="What you can do with this element">
      {rejected ? (
        // The server refuses an edit of a rejected element and names this step: propose it again with new reasons, or withdraw it.
        <Button variant="primary" onClick={() => setOpen("repropose")}>
          Propose again with new reasons
        </Button>
      ) : addTo ? (
        <>
          <p className="element-actions__note" role="note">
            {addTo} has a pending change here: add to it, or wait.{addedBy.length > 0 ? ` Added to by ${addedBy.join(", ")}.` : ""}
          </p>
          <Link className="button button--primary" to={editPath(element.id)}>
            Add to it
          </Link>
        </>
      ) : (
        <Link className="button button--primary" to={editPath(element.id)}>
          Edit
        </Link>
      )}
      {canWithdraw(element) && (
        <Button onClick={() => setOpen("withdraw")}>Withdraw</Button>
      )}
      {canMarkRemoval(element) && <Button onClick={() => setOpen("removal")}>Mark for removal</Button>}
      {hasRemoval && <Button onClick={() => setOpen("lift")}>Take the removal mark off</Button>}
      {pendingIsMine && <Button onClick={() => setOpen("drop")}>Drop the Next version</Button>}
      {hasPending && !pendingIsMine && !addTo && (
        <p className="element-actions__note muted" role="note">
          A colleague has a pending change on this element. Wait until the SME has settled it, then make yours.
        </p>
      )}
      {canCorrectCode && <Button variant="quiet" onClick={() => setOpen("code")}>Correct the code</Button>}
      {canProposeCode && <Button variant="quiet" onClick={() => setOpen("code")}>Propose a new code</Button>}
      {loaded && !element.not_used_by_us && (
        <Button variant="quiet" onClick={() => setOpen("not_used")}>
          Not used by us
        </Button>
      )}
      {element.kind === "item" && (
        <Button variant="quiet" onClick={() => setOpen("same_item")}>
          Same item as another name
        </Button>
      )}

      {open === "repropose" && <ReproposeDialog element={element} onClose={close} />}
      {open === "withdraw" && <WithdrawDialog element={element} onClose={close} />}
      {open === "removal" && <RemovalDialog element={element} usedIn={usedIn} onClose={close} />}
      {open === "lift" && <LiftDialog element={element} onClose={close} />}
      {open === "drop" && <DropDialog element={element} onClose={close} />}
      {open === "code" && <CodeDialog element={element} onClose={close} />}
      {open === "not_used" && <NotUsedDialog element={element} onClose={close} />}
      {open === "same_item" && <SameItemDialog element={element} onClose={close} />}
    </div>
  );
}

function WithdrawDialog({ element, onClose }: { element: ElementRow; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [replacedBy, setReplacedBy] = useState("");
  const [duplicate, setDuplicate] = useState(false);
  const [dependents, setDependents] = useState<Array<{ id: Id; label: string }>>([]);
  const withdraw = useApiCommand("withdraw_element");
  const ids = useCommandId();
  const refresh = useRefresh();

  const send = (confirmed: Id[]) =>
    withdraw.mutate(
      {
        id: element.id,
        expected_version: element.version,
        confirm_dependents: confirmed,
        replaced_by_id: replacedBy || null,
        reason: reason.trim() || null,
        // a duplicate is folded into the element that replaces it: the record writes a `fold` line (CR-02)
        ...(replacedBy && duplicate ? { fold: true } : {}),
        command_id: ids.current(),
      },
      {
        onSuccess: () => {
          ids.renew();
          refresh("element", "item_page", "item_snapshot", "list_elements", "family_tree", "inbox", "session_cards");
          onClose();
        },
        onError: (error) => {
          // The server lists what hangs on the element and waits for one yes for all of it.
          if (isRefusal(error, "needs_confirmation", "conflict")) {
            const found = dependentsFromDetails(error.details);
            if (found.length > 0) {
              ids.renew();
              setDependents(found);
            }
          }
        },
      },
    );

  const needsYes = dependents.length > 0;
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title={`Withdraw ${element.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={withdraw.isPending} onClick={() => send(needsYes ? dependents.map((d) => d.id) : [])}>
            {needsYes ? "Withdraw all of these" : "Withdraw"}
          </Button>
        </>
      }
    >
      <p>It leaves the files at once and nobody needs to decide. Its own lines and values go with it.</p>
      {needsYes && (
        <div className="dependents">
          <p>These hang on it. One yes withdraws them all, because none of them was ever validated.</p>
          <ul>
            {dependents.map((dependent) => (
              <li key={dependent.id}>{dependent.label}</li>
            ))}
          </ul>
        </div>
      )}
      <TextInput label="Why do you withdraw it? (optional)" value={reason} onChange={setReason} />
      <ElementPicker label="Replaced by (optional)" kinds={[element.kind]} value={replacedBy} onChange={setReplacedBy} exclude={[element.id]} placeholder="Nothing replaces it" />
      {replacedBy && (
        <Check
          label="This is a duplicate of it"
          hint="The revision record keeps the one that replaces it and says this one was folded into it."
          checked={duplicate}
          onChange={setDuplicate}
        />
      )}
      <FormError message={withdraw.error && !needsYes ? refusalText(withdraw.error) : null} />
    </Dialog>
  );
}

function RemovalDialog({ element, usedIn, onClose }: { element: ElementRow; usedIn?: number; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const mark = useApiCommand("mark_removal");
  const ids = useCommandId();
  const refresh = useRefresh();
  const submit = () => {
    if (!reason.trim()) {
      setProblem("Say why it should be removed.");
      return;
    }
    mark.mutate(
      { id: element.id, expected_version: element.version, reason: reason.trim(), command_id: ids.current() },
      {
        onSuccess: () => {
          ids.renew();
          refresh("element", "item_page", "inbox", "sme_card");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title={`Mark ${element.name} for removal`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={mark.isPending} onClick={submit}>
            Mark for removal
          </Button>
        </>
      }
    >
      <p>It stays in the files until the SME validates the removal.{usedIn ? ` It is used in ${usedIn} ${usedIn === 1 ? "place" : "places"}.` : ""}</p>
      <div className="row quick-reasons" role="group" aria-label="Usual reasons">
        {REMOVAL_REASONS.map((text) => (
          <Button key={text} variant="quiet" onClick={() => setReason(text)}>
            {text}
          </Button>
        ))}
      </div>
      <TextArea label="Why should it be removed?" required value={reason} onChange={setReason} error={problem} />
      <FormError message={mark.error ? refusalText(mark.error) : null} />
    </Dialog>
  );
}

function LiftDialog({ element, onClose }: { element: ElementRow; onClose: () => void }) {
  const lift = useApiCommand("lift_removal");
  const refresh = useRefresh();
  return (
    <ConfirmDialog
      open
      title="Take the removal mark off?"
      message={`${element.name} stays as it is.`}
      confirmLabel="Take it off"
      busy={lift.isPending}
      error={lift.error ? refusalText(lift.error) : null}
      onCancel={onClose}
      onConfirm={() =>
        lift.mutate(
          { id: element.id, expected_version: element.version },
          {
            onSuccess: () => {
              refresh("element", "item_page", "inbox", "sme_card");
              onClose();
            },
          },
        )
      }
    />
  );
}

function DropDialog({ element, onClose }: { element: ElementRow; onClose: () => void }) {
  const drop = useApiCommand("drop_next_version");
  const refresh = useRefresh();
  return (
    <ConfirmDialog
      open
      title="Drop the Next version?"
      message="What the SME validated stays official. The pending change you made is thrown away."
      confirmLabel="Drop it"
      busy={drop.isPending}
      error={drop.error ? refusalText(drop.error) : null}
      onCancel={onClose}
      onConfirm={() =>
        drop.mutate(
          { id: element.id, expected_version: element.version },
          {
            onSuccess: () => {
              refresh("element", "item_page", "inbox", "sme_card");
              onClose();
            },
          },
        )
      }
    />
  );
}

function CodeDialog({ element, onClose }: { element: ElementRow; onClose: () => void }) {
  const [code, setCode] = useState(element.code);
  const [problem, setProblem] = useState<string | null>(null);
  const correct = useApiCommand("correct_code");
  const navigate = useNavigate();
  const refresh = useRefresh();
  // after the SME's first validation the new code is a Next version that goes to the SME (DEC-1016-107)
  const toSme = !!element.code_fixed;
  const title = toSme ? "Propose a new code" : "Correct the code";
  const taken = isRefusal(correct.error, "code_taken") ? correct.error : null;
  const takenBy = taken && typeof (taken.details as { element_id?: unknown } | null)?.element_id === "string" ? String((taken.details as { element_id: string }).element_id) : null;
  const submit = () => {
    if (!code.trim()) {
      setProblem("Write the code.");
      return;
    }
    correct.mutate(
      { id: element.id, expected_version: element.version, new_code: code.trim() },
      {
        onSuccess: () => {
          refresh("element", "item_page", "list_elements", "family_tree");
          if (toSme) refresh("inbox", "sme_card");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={correct.isPending} onClick={submit}>
            {toSme ? "Send it to the SME" : "Correct the code"}
          </Button>
        </>
      }
    >
      {toSme ? (
        <p>
          Propose a new code; it goes to the SME. Until the SME validates it, the files keep the code {element.code}. Once it is
          validated, {element.code} is never used again.
        </p>
      ) : (
        <p>The code is fixed when the SME validates the element. Until then you can correct it.</p>
      )}
      <TextInput label="New code" required value={code} onChange={setCode} error={problem} />
      <FormError message={correct.error ? refusalText(correct.error) : null} />
      {takenBy && (
        <Button variant="quiet" onClick={() => void navigate(`/elements/${encodeURIComponent(takenBy)}`)}>
          Open the element that uses it
        </Button>
      )}
    </Dialog>
  );
}

function NotUsedDialog({ element, onClose }: { element: ElementRow; onClose: () => void }) {
  const [note, setNote] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const record = useApiCommand("record_not_used_by_us");
  const refresh = useRefresh();
  const submit = () => {
    if (!note.trim()) {
      setProblem("Say why your discipline does not use it.");
      return;
    }
    record.mutate(
      { id: element.id, note: note.trim() },
      {
        onSuccess: () => {
          refresh("element", "item_page", "sme_card");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title={`${element.name}: not used by us`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={record.isPending} onClick={submit}>
            Record it
          </Button>
        </>
      }
    >
      <p>This is loaded content, so it is not removed. The note tells the others that your discipline does not use it.</p>
      <TextArea label="Why does your discipline not use it?" required value={note} onChange={setNote} error={problem} />
      <FormError message={record.error ? refusalText(record.error) : null} />
    </Dialog>
  );
}

function SameItemDialog({ element, onClose }: { element: ElementRow; onClose: () => void }) {
  const [text, setText] = useState(element.same_item_as ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const record = useApiCommand("set_same_item_as");
  const refresh = useRefresh();
  const submit = () => {
    if (!text.trim()) {
      setProblem("Write the other name.");
      return;
    }
    record.mutate(
      { id: element.id, text: text.trim() },
      {
        onSuccess: () => {
          refresh("element", "item_page");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title={`${element.name}: the same item under another name`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={record.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <p>Use this when another team or another library calls this item by a different name.</p>
      <TextInput label="Also called" required value={text} onChange={setText} error={problem} />
      <FormError message={record.error ? refusalText(record.error) : null} />
    </Dialog>
  );
}
